import { useState, useEffect } from "react";
import { dateLocale } from "../lib/languages";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import {
  ArrowLeft, Rocket, Shield, Moon, Radio, BatteryCharging, Zap, Target,
  Sun, Star, MessageCircle, Plus, Trash2, Save, History, ChevronDown,
  ChevronRight, Check, Sparkles
} from "lucide-react";
import { API } from "../App";
import { toast } from "sonner";
import { useSharePrompt } from "../hooks/useSharePrompt";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useI18n } from "../lib/i18n";
import { formatDateSafe } from "../lib/formatDate";
import { useTdahKidsI18n } from "../lib/tdahKidsI18n";

// ============ EXERCISE DEFINITIONS ============
const EXERCISE_IMAGES = {
  tdah_kids_superpowers: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/9588ac1fbddd04fc28f472ffd417af47104e558bb2e4a547739e2c8a2898ba5d.png",
  tdah_kids_safety_bubble: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/054a597cad769128bf104a6ea01679a74db66249ed96160f447c3fd7c70eba71.png",
  tdah_kids_relaxation: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/619267357051122d1c702e328e4e03b222df7cbf9d6d2e80d021af0c49206df1.png",
  tdah_kids_agitation_radar: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/9f98db5f04f85d31f3bd3463b37170d39af8968f8af1c0a5e6fb9ee0e1a566ec.png",
  tdah_kids_recharge: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/53367f71c31f3ea47792c7553a298327589f4bf86e6c61625e2463d292c48660.png",
  tdah_kids_turbo_brain: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/88ba31049edd87165405ef3045a910fae963bcc6b6815672dd76912f93e27fc5.png",
  tdah_kids_mission: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/267c19f1b860035d7d4fa668946f117c01070e7ea94afb1aaada5f83b64adcc6.png",
  tdah_kids_superhero_day: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/c8d5a37987f5a504cec981b0c6276b12fc3fbebf57f82fdd4dca2e5fdbc7dd64.png",
  tdah_kids_victories: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/bbb2f60fa68d7cb207687e63e492c6c7773a9925d799c936a4003096e0807a0a.png",
  tdah_kids_therapist_questions: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/0ab92f37417b8db60bc50671e15c4467547b782a7f77ea5153365319de2e3d62.png",
};

// ============ EXERCISE DEFINITIONS (locale-aware) ============
const getExercises = (L) => ({
  tdah_kids_superpowers: { title: L.superpowers_title, subtitle: L.superpowers_subtitle, icon: Rocket, gradient: "from-violet-600 to-indigo-700", accent: "violet" },
  tdah_kids_safety_bubble: { title: L.safety_bubble_title, subtitle: L.safety_bubble_subtitle, icon: Shield, gradient: "from-cyan-600 to-teal-700", accent: "cyan" },
  tdah_kids_relaxation: { title: L.relaxation_title, subtitle: L.relaxation_subtitle, icon: Moon, gradient: "from-indigo-600 to-purple-700", accent: "indigo" },
  tdah_kids_agitation_radar: { title: L.agitation_radar_title, subtitle: L.agitation_radar_subtitle, icon: Radio, gradient: "from-rose-600 to-pink-700", accent: "rose" },
  tdah_kids_recharge: { title: L.recharge_title, subtitle: L.recharge_subtitle, icon: BatteryCharging, gradient: "from-emerald-600 to-green-700", accent: "emerald" },
  tdah_kids_turbo_brain: { title: L.turbo_brain_title, subtitle: L.turbo_brain_subtitle, icon: Zap, gradient: "from-amber-500 to-orange-600", accent: "amber" },
  tdah_kids_mission: { title: L.mission_title, subtitle: L.mission_subtitle, icon: Target, gradient: "from-red-600 to-rose-700", accent: "red" },
  tdah_kids_superhero_day: { title: L.superhero_day_title, subtitle: L.superhero_day_subtitle, icon: Sun, gradient: "from-sky-500 to-blue-600", accent: "sky" },
  tdah_kids_victories: { title: L.victories_title, subtitle: L.victories_subtitle, icon: Star, gradient: "from-yellow-500 to-amber-600", accent: "yellow" },
  tdah_kids_therapist_questions: { title: L.therapist_questions_title, subtitle: L.therapist_questions_subtitle, icon: MessageCircle, gradient: "from-blue-600 to-indigo-700", accent: "blue" },
});

// ============ SHARED COMPONENTS ============
const GalacticHeader = ({ exerciseDef, Icon, exerciseType }) => (
  <div className={`relative overflow-hidden rounded-xl bg-gradient-to-r ${exerciseDef.gradient} p-6 text-white mb-6`}>
    <div className="absolute inset-0 opacity-20" style={{
      backgroundImage: "radial-gradient(1px 1px at 20px 30px, white, transparent), radial-gradient(1px 1px at 40px 70px, white, transparent), radial-gradient(1px 1px at 50px 160px, white, transparent), radial-gradient(1px 1px at 90px 40px, white, transparent), radial-gradient(1px 1px at 130px 80px, white, transparent), radial-gradient(1px 1px at 160px 120px, white, transparent)",
      backgroundSize: "200px 200px"
    }} />
    <div className="relative flex items-center gap-4">
      {EXERCISE_IMAGES[exerciseType] ? (
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden bg-white/10 backdrop-blur flex-shrink-0">
          <img src={EXERCISE_IMAGES[exerciseType]} alt={exerciseDef.title} className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
          <Icon className="w-7 h-7" />
        </div>
      )}
      <div>
        <h2 className="text-xl sm:text-2xl font-bold">{exerciseDef.title}</h2>
        <p className="text-white/80 text-sm mt-0.5">{exerciseDef.subtitle}</p>
      </div>
    </div>
  </div>
);

const HistoryPanel = ({ history, exerciseType, L }) => {
  const { locale } = useI18n();
  if (!history.length) return <p className="text-center text-muted-foreground py-4 text-sm">{L.no_history}</p>;
  return (
    <div className="space-y-2 max-h-64 overflow-y-auto">
      {history.map((entry, i) => (
        <div key={i} className="p-3 rounded-lg border text-sm" data-testid={`history-${exerciseType}-${i}`}>
          <p className="text-xs text-muted-foreground">{formatDateSafe(entry.created_at, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }, dateLocale(locale))}</p>
          {exerciseType === "tdah_kids_victories" && <p className="mt-1 font-medium">{entry.data?.victory}</p>}
          {exerciseType === "tdah_kids_therapist_questions" && <p className="mt-1">{(entry.data?.questions || []).join(", ")}</p>}
          {exerciseType === "tdah_kids_agitation_radar" && <p className="mt-1">{L.hist_intensity}: {entry.data?.intensity}/10 — {entry.data?.activity}</p>}
          {exerciseType === "tdah_kids_mission" && <p className="mt-1">{entry.data?.scenario}</p>}
          {exerciseType === "tdah_kids_recharge" && <p className="mt-1">{entry.data?.activity}</p>}
          {exerciseType === "tdah_kids_superhero_day" && <p className="mt-1">{(entry.data?.periods || []).length} {L.hist_periods}</p>}
          {!["tdah_kids_victories","tdah_kids_therapist_questions","tdah_kids_agitation_radar","tdah_kids_mission","tdah_kids_recharge","tdah_kids_superhero_day"].includes(exerciseType) && (
            <p className="mt-1 text-muted-foreground truncate">{JSON.stringify(entry.data).slice(0, 80)}...</p>
          )}
        </div>
      ))}
    </div>
  );
};

// ============ EXERCISE FORMS ============

const SuperpowersForm = ({ onSave, saving, L }) => {
  const presets = [L.sp_preset_1, L.sp_preset_2, L.sp_preset_3, L.sp_preset_4];
  const [known, setKnown] = useState(presets);
  const [custom, setCustom] = useState([""]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base">{L.sp_known}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {known.map((p, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800">
              <Sparkles className="w-4 h-4 text-violet-500 flex-shrink-0" />
              <span className="text-sm">{p}</span>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">{L.sp_custom}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {custom.map((val, i) => (
            <div key={i} className="flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <Input value={val} onChange={e => { const c = [...custom]; c[i] = e.target.value; setCustom(c); }} placeholder={`${L.sp_placeholder} ${i + 1}...`} data-testid={`superpower-input-${i}`} />
              {custom.length > 1 && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCustom(custom.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5" /></Button>}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setCustom([...custom, ""])} className="mt-2"><Plus className="w-3.5 h-3.5 mr-1" /> {L.add}</Button>
        </CardContent>
      </Card>
      <Button onClick={() => onSave({ superpowers_known: known, superpowers_custom: custom.filter(Boolean) })} disabled={saving} className="w-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-superpowers">
        <Save className="w-4 h-4 mr-2" />{saving ? "..." : L.sp_save}
      </Button>
    </div>
  );
};

const SafetyBubbleForm = ({ onSave, saving, L }) => {
  const [form, setForm] = useState({ color: "", shape: "", texture: "", activities: "", noise_level: "silence", signal: "" });
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-4">
      <Card><CardContent className="pt-6 space-y-4">
        <div><label className="text-sm font-medium">{L.bubble_color}</label><p className="text-xs text-muted-foreground mb-1">{L.bubble_color_hint}</p>
          <Input value={form.color} onChange={e => set("color", e.target.value)} placeholder={L.bubble_color_ph} data-testid="bubble-color" /></div>
        <div><label className="text-sm font-medium">{L.bubble_shape}</label><p className="text-xs text-muted-foreground mb-1">{L.bubble_shape_hint}</p>
          <Input value={form.shape} onChange={e => set("shape", e.target.value)} placeholder={L.bubble_shape_ph} data-testid="bubble-shape" /></div>
        <div><label className="text-sm font-medium">{L.bubble_texture}</label><p className="text-xs text-muted-foreground mb-1">{L.bubble_texture_hint}</p>
          <Input value={form.texture} onChange={e => set("texture", e.target.value)} placeholder={L.bubble_texture_ph} data-testid="bubble-texture" /></div>
        <div><label className="text-sm font-medium">{L.bubble_activities}</label><p className="text-xs text-muted-foreground mb-1">{L.bubble_activities_hint}</p>
          <Textarea value={form.activities} onChange={e => set("activities", e.target.value)} placeholder={L.bubble_activities_ph} className="min-h-[60px]" data-testid="bubble-activities" /></div>
        <div><label className="text-sm font-medium">{L.bubble_noise}</label>
          <div className="flex gap-2 mt-1">{[{k: "silence", l: L.bubble_noise_silence}, {k: "un peu", l: L.bubble_noise_low}, {k: "moyen", l: L.bubble_noise_medium}].map(n => (
            <Button key={n.k} variant={form.noise_level === n.k ? "default" : "outline"} size="sm" onClick={() => set("noise_level", n.k)} data-testid={`noise-${n.k}`}>{n.l}</Button>
          ))}</div></div>
        <div><label className="text-sm font-medium">{L.bubble_signal}</label><p className="text-xs text-muted-foreground mb-1">{L.bubble_signal_hint}</p>
          <Input value={form.signal} onChange={e => set("signal", e.target.value)} placeholder={L.bubble_signal_ph} data-testid="bubble-signal" /></div>
      </CardContent></Card>
      <Button onClick={() => onSave(form)} disabled={saving} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="save-bubble">
        <Save className="w-4 h-4 mr-2" />{saving ? "..." : L.bubble_save}
      </Button>
    </div>
  );
};

const RelaxationForm = ({ onSave, saving, L }) => {
  const steps = [
    { label: L.relax_s1, body: L.relax_hands }, { label: L.relax_s2, body: L.relax_hands },
    { label: L.relax_s3, body: L.relax_arms }, { label: L.relax_s4, body: L.relax_arms },
    { label: L.relax_s5, body: L.relax_legs }, { label: L.relax_s6, body: L.relax_legs },
    { label: L.relax_s7, body: L.relax_belly }, { label: L.relax_s8, body: L.relax_belly },
    { label: L.relax_s9, body: L.relax_face }, { label: L.relax_s10, body: L.relax_face },
  ];
  const [completed, setCompleted] = useState([]);
  const [feeling, setFeeling] = useState("");
  const toggle = (i) => setCompleted(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  return (
    <div className="space-y-4">
      <Card><CardHeader><CardTitle className="text-base">{L.relax_steps_title}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {steps.map((s, i) => (
            <div key={i} onClick={() => toggle(i)} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${completed.includes(i) ? "bg-indigo-50 border-indigo-200 dark:bg-indigo-950/30 dark:border-indigo-800" : "hover:bg-muted/50"}`} data-testid={`relax-step-${i}`}>
              <div className={`w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${completed.includes(i) ? "bg-indigo-500 border-indigo-500 text-white" : "border-muted-foreground/30"}`}>
                {completed.includes(i) && <Check className="w-3 h-3" />}
              </div>
              <div><span className="text-xs text-muted-foreground">{s.body}</span><p className="text-sm">{s.label}</p></div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card><CardHeader><CardTitle className="text-base">{L.relax_after}</CardTitle></CardHeader>
        <CardContent>
          <Textarea value={feeling} onChange={e => setFeeling(e.target.value)} placeholder={L.relax_after_ph} className="min-h-[60px]" data-testid="relax-feeling" />
        </CardContent>
      </Card>
      <Button onClick={() => onSave({ steps_completed: completed, feeling_after: feeling })} disabled={saving} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-relaxation">
        <Save className="w-4 h-4 mr-2" />{saving ? "..." : L.save}
      </Button>
    </div>
  );
};

const AgitationRadarForm = ({ onSave, saving, L }) => {
  const [form, setForm] = useState({ location: "", activity: "", sensations: "", intensity: [3], boredom: [3], urge: [3], action_taken: "", feeling_after: "" });
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-4">
      <Card><CardContent className="pt-6 space-y-4">
        <div><label className="text-sm font-medium">{L.radar_location}</label>
          <Input value={form.location} onChange={e => set("location", e.target.value)} placeholder={L.radar_location_ph} data-testid="radar-location" /></div>
        <div><label className="text-sm font-medium">{L.radar_sensations}</label>
          <Textarea value={form.sensations} onChange={e => set("sensations", e.target.value)} placeholder={L.radar_sensations_ph} className="min-h-[60px]" data-testid="radar-sensations" /></div>
        <div><label className="text-sm font-medium">{L.radar_intensity}</label>
          <div className="flex items-center gap-3"><Slider value={form.intensity} onValueChange={v => set("intensity", v)} min={1} max={10} step={1} className="flex-1" data-testid="radar-intensity" /><span className="text-lg font-bold w-8 text-center">{form.intensity[0]}</span></div></div>
        <div><label className="text-sm font-medium">{L.radar_boredom}</label>
          <div className="flex items-center gap-3"><Slider value={form.boredom} onValueChange={v => set("boredom", v)} min={1} max={10} step={1} className="flex-1" /><span className="text-lg font-bold w-8 text-center">{form.boredom[0]}</span></div></div>
        <div><label className="text-sm font-medium">{L.radar_urge}</label>
          <div className="flex items-center gap-3"><Slider value={form.urge} onValueChange={v => set("urge", v)} min={1} max={10} step={1} className="flex-1" /><span className="text-lg font-bold w-8 text-center">{form.urge[0]}</span></div></div>
        <div><label className="text-sm font-medium">{L.radar_action}</label>
          <Textarea value={form.action_taken} onChange={e => set("action_taken", e.target.value)} placeholder={L.radar_action_ph} className="min-h-[60px]" data-testid="radar-action" /></div>
        <div><label className="text-sm font-medium">{L.radar_after}</label>
          <Input value={form.feeling_after} onChange={e => set("feeling_after", e.target.value)} placeholder={L.radar_after_ph} data-testid="radar-after" /></div>
      </CardContent></Card>
      <Button onClick={() => onSave({ ...form, intensity: form.intensity[0], boredom: form.boredom[0], urge: form.urge[0] })} disabled={saving} className="w-full bg-rose-600 hover:bg-rose-700 text-white" data-testid="save-radar">
        <Save className="w-4 h-4 mr-2" />{saving ? "..." : L.save}
      </Button>
    </div>
  );
};

const RechargeForm = ({ onSave, saving, L }) => {
  const [form, setForm] = useState({ activity: "", feeling_during: "", feeling_after: "" });
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-4">
      <Card><CardContent className="pt-6 space-y-4">
        <div><label className="text-sm font-medium">{L.recharge_what}</label><p className="text-xs text-muted-foreground mb-1">{L.recharge_what_hint}</p>
          <Input value={form.activity} onChange={e => set("activity", e.target.value)} placeholder={L.recharge_what_ph} data-testid="recharge-activity" /></div>
        <div><label className="text-sm font-medium">{L.recharge_during}</label>
          <Textarea value={form.feeling_during} onChange={e => set("feeling_during", e.target.value)} placeholder={L.recharge_during_ph} className="min-h-[60px]" data-testid="recharge-during" /></div>
        <div><label className="text-sm font-medium">{L.recharge_after}</label>
          <Textarea value={form.feeling_after} onChange={e => set("feeling_after", e.target.value)} placeholder={L.recharge_after_ph} className="min-h-[60px]" data-testid="recharge-after" /></div>
      </CardContent></Card>
      <Button onClick={() => onSave(form)} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-recharge">
        <Save className="w-4 h-4 mr-2" />{saving ? "..." : L.save}
      </Button>
    </div>
  );
};

const TurboBrainForm = ({ onSave, saving, L }) => {
  const [turboHelps, setTurboHelps] = useState([L.turbo_preset_1, L.turbo_preset_2, L.turbo_preset_3]);
  const [brakeTired, setBrakeTired] = useState([L.turbo_brake_1, L.turbo_brake_2, L.turbo_brake_3]);
  const [customHelps, setCustomHelps] = useState([""]);
  const [customBrake, setCustomBrake] = useState([""]);

  return (
    <div className="space-y-4">
      <Card><CardHeader><CardTitle className="text-base">{L.turbo_helps}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {turboHelps.map((h, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <Zap className="w-4 h-4 text-amber-500 flex-shrink-0" /><span className="text-sm">{h}</span>
            </div>
          ))}
          {customHelps.map((val, i) => (
            <div key={`c${i}`} className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400 flex-shrink-0" />
              <Input value={val} onChange={e => { const c = [...customHelps]; c[i] = e.target.value; setCustomHelps(c); }} placeholder={L.other} />
              {customHelps.length > 1 && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCustomHelps(customHelps.filter((_, j) => j !== i))}><Trash2 className="w-3 h-3" /></Button>}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setCustomHelps([...customHelps, ""])}><Plus className="w-3 h-3 mr-1" /> {L.add}</Button>
        </CardContent>
      </Card>
      <Card><CardHeader><CardTitle className="text-base">{L.turbo_brake}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {brakeTired.map((b, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
              <Moon className="w-4 h-4 text-red-400 flex-shrink-0" /><span className="text-sm">{b}</span>
            </div>
          ))}
          {customBrake.map((val, i) => (
            <div key={`b${i}`} className="flex items-center gap-2">
              <Moon className="w-4 h-4 text-red-300 flex-shrink-0" />
              <Input value={val} onChange={e => { const c = [...customBrake]; c[i] = e.target.value; setCustomBrake(c); }} placeholder={L.other} />
              {customBrake.length > 1 && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCustomBrake(customBrake.filter((_, j) => j !== i))}><Trash2 className="w-3 h-3" /></Button>}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setCustomBrake([...customBrake, ""])}><Plus className="w-3 h-3 mr-1" /> {L.add}</Button>
        </CardContent>
      </Card>
      <Button onClick={() => onSave({ turbo_helps: [...turboHelps, ...customHelps.filter(Boolean)], brake_tired: [...brakeTired, ...customBrake.filter(Boolean)] })} disabled={saving} className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-turbo">
        <Save className="w-4 h-4 mr-2" />{saving ? "..." : L.save}
      </Button>
    </div>
  );
};

const MissionForm = ({ onSave, saving, L }) => {
  const [form, setForm] = useState({ scenario: "", usual_behavior: "", turbo_says: "", outcome: "", mission_activity: "", result: "" });
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-4">
      <Card><CardContent className="pt-6 space-y-4">
        <div><label className="text-sm font-medium">{L.mission_scenario}</label><p className="text-xs text-muted-foreground mb-1">{L.mission_scenario_hint}</p>
          <Input value={form.scenario} onChange={e => set("scenario", e.target.value)} placeholder={L.mission_scenario_ph} data-testid="mission-scenario" /></div>
        <div><label className="text-sm font-medium">{L.mission_usual}</label>
          <Textarea value={form.usual_behavior} onChange={e => set("usual_behavior", e.target.value)} placeholder={L.mission_usual_ph} className="min-h-[50px]" data-testid="mission-usual" /></div>
        <div><label className="text-sm font-medium">{L.mission_turbo}</label>
          <Textarea value={form.turbo_says} onChange={e => set("turbo_says", e.target.value)} placeholder={L.mission_turbo_ph} className="min-h-[50px]" data-testid="mission-turbo" /></div>
        <div><label className="text-sm font-medium">{L.mission_outcome}</label>
          <Input value={form.outcome} onChange={e => set("outcome", e.target.value)} placeholder={L.mission_outcome_ph} data-testid="mission-outcome" /></div>
        <div className="pt-3 border-t">
          <label className="text-sm font-medium text-emerald-600">{L.mission_activity}</label>
          <Input value={form.mission_activity} onChange={e => set("mission_activity", e.target.value)} placeholder={L.mission_activity_ph} className="mt-1" data-testid="mission-activity" /></div>
        <div><label className="text-sm font-medium text-emerald-600">{L.mission_result}</label>
          <Textarea value={form.result} onChange={e => set("result", e.target.value)} placeholder={L.mission_result_ph} className="min-h-[50px]" data-testid="mission-result" /></div>
      </CardContent></Card>
      <Button onClick={() => onSave(form)} disabled={saving} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="save-mission">
        <Save className="w-4 h-4 mr-2" />{saving ? "..." : L.mission_done}
      </Button>
    </div>
  );
};

const SuperheroDayForm = ({ onSave, saving, L }) => {
  const defaultPeriods = [
    { name: L.day_morning, energy: 5, need: "" }, { name: L.day_commute, energy: 5, need: "" },
    { name: L.day_class_am, energy: 5, need: "" }, { name: L.day_lunch, energy: 5, need: "" },
    { name: L.day_afternoon, energy: 5, need: "" }, { name: L.day_sport, energy: 5, need: "" },
    { name: L.day_evening, energy: 5, need: "" }, { name: L.day_bedtime, energy: 5, need: "" },
  ];
  const [periods, setPeriods] = useState(defaultPeriods);
  const [notes, setNotes] = useState("");
  const needs = [L.day_need_move, L.day_need_talk, L.day_need_calm, L.day_need_hug];
  const updatePeriod = (i, key, val) => { const p = [...periods]; p[i] = { ...p[i], [key]: val }; setPeriods(p); };
  const getEnergyColor = (e) => e >= 7 ? "bg-emerald-500" : e >= 4 ? "bg-amber-500" : "bg-red-500";
  const getEnergyLabel = (e) => e >= 7 ? L.day_full : e >= 4 ? L.day_medium : L.day_empty;

  return (
    <div className="space-y-4">
      <Card><CardHeader><CardTitle className="text-base">{L.day_energy}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {periods.map((p, i) => (
            <div key={i} className="p-3 rounded-lg border space-y-2" data-testid={`period-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{p.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full text-white ${getEnergyColor(p.energy)}`}>{getEnergyLabel(p.energy)}</span>
              </div>
              <div className="flex items-center gap-2">
                <BatteryCharging className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <Slider value={[p.energy]} onValueChange={v => updatePeriod(i, "energy", v[0])} min={1} max={10} step={1} className="flex-1" />
                <span className="text-sm font-bold w-6 text-center">{p.energy}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {needs.map(n => (
                  <Button key={n} variant={p.need === n ? "default" : "outline"} size="sm" className="h-6 text-xs px-2" onClick={() => updatePeriod(i, "need", n)}>{n}</Button>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card><CardHeader><CardTitle className="text-base">{L.notes}</CardTitle></CardHeader>
        <CardContent><Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder={L.notes_placeholder} className="min-h-[60px]" /></CardContent>
      </Card>
      <Button onClick={() => onSave({ periods, notes })} disabled={saving} className="w-full bg-sky-600 hover:bg-sky-700 text-white" data-testid="save-day">
        <Save className="w-4 h-4 mr-2" />{saving ? "..." : L.day_save}
      </Button>
    </div>
  );
};

const VictoriesForm = ({ onSave, saving, L }) => {
  const [victory, setVictory] = useState("");
  const [proud, setProud] = useState("");
  return (
    <div className="space-y-4">
      <Card><CardContent className="pt-6 space-y-4">
        <div><label className="text-sm font-medium">{L.victory_title}</label>
          <p className="text-xs text-muted-foreground mb-2">{L.victory_examples}</p>
          <Textarea value={victory} onChange={e => setVictory(e.target.value)} placeholder={L.victory_ph} className="min-h-[80px]" data-testid="victory-text" /></div>
        <div><label className="text-sm font-medium">{L.victory_proud}</label>
          <Textarea value={proud} onChange={e => setProud(e.target.value)} placeholder={L.victory_proud_ph} className="min-h-[60px]" data-testid="victory-proud" /></div>
      </CardContent></Card>
      <Button onClick={() => onSave({ victory, proud_because: proud })} disabled={saving || !victory.trim()} className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-victory">
        <Star className="w-4 h-4 mr-2" />{saving ? "..." : L.victory_save}
      </Button>
    </div>
  );
};

const TherapistQuestionsForm = ({ onSave, saving, latestEntry, L }) => {
  const [questions, setQuestions] = useState(latestEntry?.data?.questions || [""]);
  return (
    <div className="space-y-4">
      <Card><CardHeader><CardTitle className="text-base">{L.questions_title}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {questions.map((q, i) => (
            <div key={i} className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <Input value={q} onChange={e => { const qs = [...questions]; qs[i] = e.target.value; setQuestions(qs); }} placeholder={`${L.questions_ph} ${i + 1}...`} data-testid={`question-input-${i}`} />
              {questions.length > 1 && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setQuestions(questions.filter((_, j) => j !== i))}><Trash2 className="w-3 h-3" /></Button>}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setQuestions([...questions, ""])} className="mt-1"><Plus className="w-3 h-3 mr-1" /> {L.questions_add}</Button>
        </CardContent>
      </Card>
      <Button onClick={() => onSave({ questions: questions.filter(Boolean) })} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-questions">
        <Save className="w-4 h-4 mr-2" />{saving ? "..." : L.questions_save}
      </Button>
    </div>
  );
};

// ============ MAIN PAGE ============
const TdahKidsExercisePage = ({ user, logout }) => {
  const { exerciseType } = useParams();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [latestEntry, setLatestEntry] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const { locale } = useI18n();
  const L = useTdahKidsI18n(locale);
  const EXERCISES = getExercises(L);

  const exerciseDef = EXERCISES[exerciseType];

  useEffect(() => {
    if (!exerciseDef) return;
    const loadData = async () => {
      try {
        const [historyRes, latestRes] = await Promise.all([
          axios.get(`${API}/tdah-kids/${exerciseType}/entries?limit=10`),
          axios.get(`${API}/tdah-kids/${exerciseType}/latest`),
        ]);
        setHistory(historyRes.data || []);
        setLatestEntry(latestRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    loadData();
  }, [exerciseType, exerciseDef]);

  if (!exerciseDef) {
    return <DashboardLayout user={user} logout={logout}><p className="text-center py-12">{L.exercise_not_found}</p></DashboardLayout>;
  }

  const Icon = exerciseDef.icon;

  const handleSave = async (data) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/tdah-kids/${exerciseType}`, { data });
      toast.success(L.saved_success);
      promptShare(res.data, exerciseType);
      // Refresh
      const [historyRes, latestRes] = await Promise.all([
        axios.get(`${API}/tdah-kids/${exerciseType}/entries?limit=10`),
        axios.get(`${API}/tdah-kids/${exerciseType}/latest`),
      ]);
      setHistory(historyRes.data || []);
      setLatestEntry(latestRes.data);
    } catch (err) {
      toast.error(L.save_error);
      console.error(err);
    } finally { setSaving(false); }
  };

  const renderForm = () => {
    switch (exerciseType) {
      case "tdah_kids_superpowers": return <SuperpowersForm onSave={handleSave} saving={saving} L={L} />;
      case "tdah_kids_safety_bubble": return <SafetyBubbleForm onSave={handleSave} saving={saving} L={L} />;
      case "tdah_kids_relaxation": return <RelaxationForm onSave={handleSave} saving={saving} L={L} />;
      case "tdah_kids_agitation_radar": return <AgitationRadarForm onSave={handleSave} saving={saving} L={L} />;
      case "tdah_kids_recharge": return <RechargeForm onSave={handleSave} saving={saving} L={L} />;
      case "tdah_kids_turbo_brain": return <TurboBrainForm onSave={handleSave} saving={saving} L={L} />;
      case "tdah_kids_mission": return <MissionForm onSave={handleSave} saving={saving} L={L} />;
      case "tdah_kids_superhero_day": return <SuperheroDayForm onSave={handleSave} saving={saving} L={L} />;
      case "tdah_kids_victories": return <VictoriesForm onSave={handleSave} saving={saving} L={L} />;
      case "tdah_kids_therapist_questions": return <TherapistQuestionsForm onSave={handleSave} saving={saving} latestEntry={latestEntry} L={L} />;
      default: return null;
    }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-4 max-w-2xl mx-auto" data-testid={`tdah-kids-${exerciseType}`}>
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)} data-testid="toggle-history">
            <History className="w-4 h-4 mr-1" /> {L.history} ({history.length})
          </Button>
        </div>

        <GalacticHeader exerciseDef={exerciseDef} Icon={Icon} exerciseType={exerciseType} />

        {loading ? (
          <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}</div>
        ) : (
          <>
            {showHistory && (
              <Card className="mb-4">
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> {L.history}</CardTitle></CardHeader>
                <CardContent><HistoryPanel history={history} exerciseType={exerciseType} L={L} /></CardContent>
              </Card>
            )}
            {renderForm()}
          </>
        )}

        <ShareExerciseDialog
          open={!!shareData}
          entryId={shareData?.entryId}
          exerciseType={shareData?.exerciseType}
          onClose={closeSharePrompt}
        />
      </div>
    </DashboardLayout>
  );
};

export default TdahKidsExercisePage;
